import React, { useMemo, useState } from 'react';
import {
  Activity, TrendingUp, TrendingDown, Target, UserCheck,
  BarChart3, AlertTriangle, CheckCircle, Users, Crosshair,
  Brain, ArrowRight, Layers, ShieldAlert, Sparkles, Scale,
  Vote, MoveRight, LineChart, Map as MapIcon, Footprints
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

// Existing Components
import { ElectionResultsChart } from "../Charts";
import { partyColors, getPartyColor, getPartyTextColor } from "../Constants";

// ====================================================================================
// SUB-COMPONENT: MICRO-STAT (HUD STYLE)
// ====================================================================================
const MicroStat = ({ label, value, subtext, icon: Icon, color = "text-slate-200", trend }) => (
    <div className="bg-slate-900/40 border border-slate-700/50 rounded-lg p-3 flex items-start justify-between group hover:border-slate-600 transition-colors">
        <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{label}</div>
            <div className={`text-xl font-bold ${color} tabular-nums tracking-tight`}>{value}</div>
            {subtext && <div className="text-[10px] text-slate-300 font-mono mt-0.5">{subtext}</div>}
        </div>
        <div className={`p-1.5 rounded-md bg-slate-800/50 ${color.replace('text-', 'text-opacity-80 ')}`}>
            <Icon size={14} />
        </div>
    </div>
);

// ====================================================================================
// SUB-COMPONENT: GLASS CONTAINER
// ====================================================================================
const GlassContainer = ({ title, icon: Icon, children, className = "" }) => (
    <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl relative flex flex-col ${className}`}>
        {/* Header */}
        <div className="p-4 border-b border-slate-800/50 flex items-center gap-2 flex-shrink-0">
            {Icon && <Icon size={16} className="text-indigo-400" />}
            <h3 className="text-xs font-bold text-slate-200 uppercase tracking-widest">{title}</h3>
        </div>
        {/* Content */}
        <div className="p-4 flex-grow relative">
            {children}
        </div>
    </div>
);

// ====================================================================================
// SUB-COMPONENT: ORACLE PROJECTION ROW
// ====================================================================================
const OracleProjectionRow = ({ party, prediction, interval, baseline }) => {
    // Format the label: remove underscores and spaces, handle casing
    const formattedLabel = party.replace(/_/g, ' ');
    
    // Normalize key for color lookup (handle lowercase keys from ground data)
    const lookupKey = party.toUpperCase();
    
    let pColor = getPartyColor(lookupKey);
    if (!pColor || pColor === "#94a3b8") { 
        pColor = getPartyColor(formattedLabel) || pColor;
    }

    const safePred = prediction || 0;
    const safeBase = baseline || 0;
    const diff = safePred - safeBase;
    const isGain = diff > 0;
    
    // Only show swing badge if we have a baseline and it's not a new entry (0 baseline)
    const showSwing = safeBase > 0.1; 

    return (
        <div className="py-1.5 border-b border-slate-800/50 last:border-0 relative group">
            {/* 1. Header Row (Label + Value) */}
            <div className="flex justify-between items-end mb-1 relative z-10">
                <div className="flex items-center gap-2 max-w-[50%]">
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: pColor }} />
                    <span className="text-xs font-bold text-slate-200 uppercase truncate">{formattedLabel}</span>
                </div>
                <div className="flex items-center gap-3">
                    {showSwing && (
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${isGain ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
                            {isGain ? '+' : ''}{diff.toFixed(1)}%
                        </span>
                    )}
                    <span className="text-sm font-bold text-white tabular-nums min-w-[3ch] text-right">{safePred.toFixed(1)}%</span>
                </div>
            </div>
            
            {/* 2. Bar Container */}
            <div className="relative">
                {/* TOOLTIP: Only renders if interval exists, floats above the bar */}
                {interval && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-50 pointer-events-none whitespace-nowrap">
                        <div className="bg-slate-900/95 backdrop-blur-md text-slate-200 text-[9px] font-mono px-2 py-1 rounded border border-slate-600 shadow-xl flex items-center gap-2">
                            <span>L: <span className="text-slate-400">{interval[0]}%</span></span>
                            <span className="w-px h-2 bg-slate-700"></span>
                            <span>H: <span className="text-slate-400">{interval[1]}%</span></span>
                        </div>
                        {/* Little Arrow */}
                        <div className="w-1.5 h-1.5 bg-slate-600 rotate-45 absolute -bottom-0.5 left-1/2 -translate-x-1/2 border-r border-b border-slate-900"></div>
                    </div>
                )}

                <div className="relative h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    {showSwing && (
                        <div 
                            className="absolute top-0 bottom-0 w-0.5 bg-white/20 z-20" 
                            style={{ left: `${Math.min(safeBase, 100)}%` }} 
                        />
                    )}
                    <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(safePred, 100)}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="h-full rounded-full opacity-80"
                        style={{ backgroundColor: pColor }}
                    />
                </div>
            </div>
        </div>
    );
};

// ====================================================================================
// SUB-COMPONENT: WINABILITY GAUGE (Updated Colors)
// ====================================================================================
const WinabilityReactor = ({ score, confidence }) => {
    const percentage = Math.round((score || 0) * 100);
    
    // Determine Color State (Red < 30, Amber < 50, Green >= 50)
    let colorClass = "text-emerald-500";
    let strokeColor = "#10b981";
    let bgBlur = "bg-emerald-500";

    if (percentage < 30) {
        colorClass = "text-rose-500";
        strokeColor = "#ef4444";
        bgBlur = "bg-rose-500";
    } else if (percentage < 50) {
        colorClass = "text-amber-500";
        strokeColor = "#f59e0b";
        bgBlur = "bg-amber-500";
    }
    
    return (
        <div className="relative flex flex-col items-center justify-center p-6">
            <div className={`absolute inset-0 rounded-full blur-3xl opacity-10 ${bgBlur}`} />
            
            <div className="relative w-48 h-48">
                <svg className="w-full h-full transform -rotate-90">
                    <circle cx="96" cy="96" r="88" stroke="#1e293b" strokeWidth="8" fill="transparent" />
                    <motion.circle 
                        initial={{ strokeDashoffset: 553 }}
                        animate={{ strokeDashoffset: 553 - (553 * (score || 0)) }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                        cx="96" cy="96" r="88" 
                        stroke={strokeColor} 
                        strokeWidth="8" 
                        fill="transparent" 
                        strokeDasharray={553} 
                        strokeLinecap="round"
                        className="drop-shadow-[0_0_8px_rgba(0,0,0,0.5)]"
                    />
                </svg>
                
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Probability</div>
                    <div className={`text-5xl font-bold tracking-tighter ${colorClass} drop-shadow-md`}>
                        {percentage}<span className="text-2xl opacity-50">%</span>
                    </div>
                    
                    <div className="mt-2 flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/50 border border-slate-700">
                        <Activity size={12} className={colorClass} />
                        <span className="text-[10px] font-mono text-slate-300">
                            Conf: {(confidence * 100).toFixed(0)}%
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ====================================================================================
// HELPER: TIER STYLE CALCULATOR
// ====================================================================================
const getTierStyle = (tier) => {
    const t = (tier || '').toUpperCase();
    if (t.includes('GAIN') || t.includes('PRIORITY')) {
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.1)]';
    }
    if (t.includes('DEFEND') || t.includes('CRITICAL') || t.includes('OPPONENT')) {
        return 'bg-rose-500/10 text-rose-400 border-rose-500/20 shadow-[0_0_10px_rgba(244,63,94,0.1)]';
    }
    if (t.includes('HOLD') || t.includes('SAFE')) {
        return 'bg-amber-500/10 text-amber-400 border-amber-500/20 shadow-[0_0_10px_rgba(245,158,11,0.1)]';
    }
    return 'bg-slate-700 text-slate-400 border-slate-600';
};

// ====================================================================================
// MAIN COMPONENT
// ====================================================================================
export const Overview = ({ currentData }) => {
    
    // UI State
    const [viewMode, setViewMode] = useState('oracle'); // 'oracle' | 'ground'
    const [historyMode, setHistoryMode] = useState('ge24'); // 'ge24' | 'local'

    // Artemis Data
    const swingAnalysis = currentData?.swing_analysis || {};
    const winability = currentData?.winability || {};
    const targeting = currentData?.voter_targeting || {};
    const squeezeData = targeting?.squeeze_opportunities || {};
    const priorityArchetypes = targeting?.priority_archetypes || []; 
    const baselines = currentData?.baselines || {};

    // Oracle & Forecast Data
    const oraclePreds = currentData.predictions?.point_estimates || {};
    const oracleIntervals = currentData.predictions?.credible_intervals_95 || {};
    
    // Ground/Forecast Map Data
    const forecastMap = currentData.forecast_map || {};
    const groundPreds = forecastMap.projected_vote_shares || {};
    const forecastScenario = forecastMap.forecast_scenario;
    const hasOracle = !!currentData.predictions;
    const hasGround = !!forecastMap.projected_vote_shares;
    
    const predictionDate = new Date(currentData.predictions?.prediction_date?._seconds * 1000 || Date.now());

    // Determine Active Data Source
    const activePreds = viewMode === 'ground' ? groundPreds : oraclePreds;
    const isGroundMode = viewMode === 'ground';

    // Derived Squeeze Targets
    const topSqueezeTargets = Object.entries(squeezeData.Top_Squeeze_Target_Parties_THEIA || {})
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3);

    // Context aware margin logic
    const margin = swingAnalysis.margin_votes || 0;
    const isLeading = margin > 0;

    // Computed Tier Style
    const tierName = currentData?.strategic_priority?.tier?.replace(/_/g, ' ') || 'UNCLASSIFIED';
    const tierStyle = getTierStyle(tierName);

    // Process Historical Data with Mapping
    const historicalChartData = useMemo(() => {
        const rawData = historyMode === 'ge24' 
            ? baselines.market_GE24 
            : baselines.market_local_history;
            
        if (!rawData) return null;

        const mappedData = {};
        const partyMap = {
            'CON': 'Conservative',
            'LDM': 'Liberal Democrat',
            'LAB': 'Labour',
            'LABOUR': 'Labour',
            'GRN': 'Green',
            'GREEN': 'Green',
            'REF': 'Reform',
            'REFORM': 'Reform',
            'IND': 'Independent',
            'UKIP': 'UKIP'
        };

        Object.entries(rawData).forEach(([key, val]) => {
            const normalizedKey = key.toUpperCase();
            const fullName = partyMap[normalizedKey] || key.charAt(0).toUpperCase() + key.slice(1).toLowerCase().replace(/_/g, ' ');
            // Updated: Round to nearest integer to avoid decimals
            mappedData[fullName] = Math.round(val);
        });

        return mappedData;
    }, [baselines, historyMode]);

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-12">
            
            {/* 1. HUD ROW */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MicroStat 
                    label="Current Margin" 
                    value={`${isLeading ? '+' : ''}${margin}`} 
                    subtext={isLeading ? "Lead over other parties" : "Votes needed to flip"}
                    icon={Scale}
                    color={isLeading ? "text-emerald-400" : "text-rose-400"}
                />
                <MicroStat 
                    label="Swing Required" 
                    value={`${swingAnalysis.swing_required_pct?.toFixed(2) || 0}%`} 
                    subtext="To overcome majority"
                    icon={TrendingUp}
                    color="text-amber-400"
                />
                 <MicroStat 
                    label="Swing Pool" 
                    value={swingAnalysis.swing_pool_size?.toLocaleString() || 0} 
                    subtext="Total accessible voters"
                    icon={Users}
                    color="text-blue-400"
                />
                <MicroStat 
                    label="Competitive Index" 
                    value={swingAnalysis.competitive_index?.toFixed(2) || "0.00"} 
                    subtext="Ward Competitiveness"
                    icon={Activity}
                    color="text-purple-400"
                />
            </div>

            {/* 2. WAR ROOM ROW */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                
                {/* LEFT: WINABILITY (Custom Glass Card) */}
                <div className="xl:col-span-4 bg-slate-900/40 border border-slate-700/50 rounded-xl relative flex flex-col">
                    <div className="p-4 border-b border-slate-800/50 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Target size={16} className="text-emerald-500" />
                            <h3 className="text-xs font-bold text-slate-200 uppercase tracking-widest">Winability Assessment</h3>
                        </div>
                        {/* UPDATED: Dynamic Semantic Colors + Header Typography Style */}
                        <span className={`text-[10px] px-2 py-0.5 rounded border font-bold tracking-wider ${tierStyle}`}>
                            {tierName}
                        </span>
                    </div>

                    <div className="flex-grow flex flex-col items-center justify-center bg-gradient-to-b from-slate-900/0 to-slate-900/50">
                        <WinabilityReactor score={winability.score} confidence={winability.confidence_interval_95?.[1] - winability.confidence_interval_95?.[0]} />
                    </div>

                    <div className="p-4 border-t border-slate-800/50 bg-slate-950/30 rounded-b-xl">
                        <div className="text-[10px] font-bold text-slate-400 uppercase mb-2 flex items-center gap-1">
                            <ShieldAlert size={10} /> Critical Factors
                        </div>
                        <div className="space-y-1">
                            {(winability.risk_factors || []).slice(0, 2).map((risk, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs text-slate-400">
                                    <span className="w-1 h-1 rounded-full bg-rose-500 mt-1.5 flex-shrink-0" />
                                    <span className="leading-tight">{risk}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* RIGHT: SIMULATION (Replaced Panel with GlassContainer) */}
                <GlassContainer 
                    title="Ward Simulation" 
                    icon={Layers} 
                    className="xl:col-span-8 min-h-[400px]"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-8 h-full">
                        
                        {/* COL 1: Historical */}
                        <div className="flex flex-col border-b lg:border-b-0 lg:border-r border-slate-800/50 pb-6 lg:pb-0 lg:pr-6">
                            <div className="mb-4 flex items-center justify-between flex-shrink-0">
                                {/* UPDATED: Increased Contrast & Toggle */}
                                <span className="text-[10px] font-bold uppercase text-slate-300">Historical Baseline</span>
                                <div className="flex bg-slate-900/50 rounded-lg p-0.5 border border-slate-800">
                                    <button 
                                        onClick={() => setHistoryMode('ge24')}
                                        className={`px-2 py-1 rounded-md text-[9px] font-bold uppercase transition-all ${historyMode === 'ge24' ? 'bg-slate-700 text-indigo-300 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                                    >
                                        General '24
                                    </button>
                                    <button 
                                        onClick={() => setHistoryMode('local')}
                                        className={`px-2 py-1 rounded-md text-[9px] font-bold uppercase transition-all ${historyMode === 'local' ? 'bg-slate-700 text-emerald-300 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                                    >
                                        Local
                                    </button>
                                </div>
                            </div>
                            {historicalChartData ? (
                                <div className="flex-grow w-full min-w-0 flex items-center justify-center">
                                    <ElectionResultsChart 
                                        data={historicalChartData}
                                        colors={partyColors}
                                    />
                                </div>
                            ) : (
                                <div className="flex-grow flex items-center justify-center text-slate-600 italic text-xs">No Data</div>
                            )}
                        </div>

                        {/* COL 2: Oracle / Ground Toggle */}
                        <div className="flex flex-col pt-6 lg:pt-0 lg:pl-2 h-full overflow-hidden">
                             {/* Header & Toggle */}
                             <div className="mb-3 flex items-center justify-between flex-shrink-0">
                                <div className="flex bg-slate-900/50 rounded-lg p-0.5 border border-slate-800">
                                    <button 
                                        onClick={() => setViewMode('oracle')}
                                        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold uppercase transition-all ${!isGroundMode ? 'bg-slate-700 text-indigo-300 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                                    >
                                        <Sparkles size={10} /> Oracle
                                    </button>
                                    <button 
                                        onClick={() => setViewMode('ground')}
                                        disabled={!hasGround}
                                        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold uppercase transition-all ${isGroundMode ? 'bg-slate-700 text-emerald-300 shadow-sm' : 'text-slate-500 hover:text-slate-300 disabled:opacity-30'}`}
                                    >
                                        <Footprints size={10} /> Ground
                                    </button>
                                </div>
                                <span className="text-[10px] font-mono text-slate-500/60">
                                    {isGroundMode ? 'LIVE ACTIVITY' : (hasOracle ? predictionDate.toLocaleDateString() : 'OFFLINE')}
                                </span>
                            </div>

                            {(hasOracle || (isGroundMode && hasGround)) ? (
                                <div className="flex flex-col flex-grow overflow-hidden">
                                     {/* Header Row */}
                                     <div className="flex justify-between items-center text-[9px] font-bold text-slate-500 uppercase tracking-widest pb-2 border-b border-slate-800/50 mb-1 px-1">
                                        <span>Party</span>
                                        <span>{isGroundMode ? 'Forecast Share' : 'Oracle Share'}</span>
                                     </div>
                                     
                                     {/* Scrollable List */}
                                     <div className="overflow-y-auto pr-1 flex-grow custom-scrollbar">
                                        {Object.entries(activePreds)
                                            .sort(([, a], [, b]) => b - a)
                                            .map(([party, score]) => (
                                                <OracleProjectionRow 
                                                    key={party}
                                                    party={party}
                                                    prediction={score}
                                                    interval={!isGroundMode ? oracleIntervals[party] : null}
                                                    baseline={currentData.context?.baseline_votes?.[party.toUpperCase()] ? (currentData.context.baseline_votes[party.toUpperCase()] / currentData.context.total_voters * 100) : 0}
                                                />
                                            ))}
                                     </div>

                                     {/* SCENARIO ALERT (Ground Mode Specific) */}
                                     <AnimatePresence>
                                        {forecastScenario && isGroundMode && (
                                            <motion.div 
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                className="mt-3 p-2 rounded border border-slate-700/50 bg-slate-900/30 flex items-start gap-2"
                                            >
                                                <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <div className="text-[9px] font-bold uppercase text-slate-500 tracking-widest">Forecast Scenario</div>
                                                    <div className="text-xs font-medium text-slate-200">{forecastScenario}</div>
                                                </div>
                                            </motion.div>
                                        )}
                                     </AnimatePresence>
                                </div>
                            ) : (
                                <div className="flex-grow flex flex-col items-center justify-center text-slate-500 border-2 border-dashed border-slate-800 rounded-lg bg-slate-900/20">
                                    <Brain size={32} className="mb-2 opacity-20" />
                                    <span className="text-xs font-medium">Model Not Deployed</span>
                                    <span className="text-[10px] opacity-50 mt-1">Run inference to generate projections</span>
                                </div>
                            )}
                        </div>
                    </div>
                </GlassContainer>
            </div>

            {/* 3. TARGETING ROW (Replaced Panel with GlassContainer) */}
            <GlassContainer title="Targetable Universe Breakdown" icon={Crosshair}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    
                    {/* COL 1: FUNNEL STATS */}
                    <div className="space-y-4">
                        <div className="bg-slate-900/40 border border-slate-700/50 rounded-lg p-3 flex items-center gap-3 hover:border-slate-600 transition-colors">
                             <div className="p-2 bg-slate-800/80 rounded-md"><Target size={16} className="text-accent-400"/></div>
                             <div>
                                 <div className="text-lg font-bold text-white leading-none">{targeting.persuadable_voters?.toLocaleString() || 0}</div>
                                 <div className="text-[10px] uppercase text-slate-500 font-bold mt-1">Persuasion Targets</div>
                             </div>
                        </div>
                        
                        <div className="bg-slate-900/40 border border-slate-700/50 rounded-lg p-3 flex items-center gap-3 hover:border-slate-600 transition-colors">
                             <div className="p-2 bg-slate-800/80 rounded-md"><ArrowRight size={16} className="text-blue-400"/></div>
                             <div>
                                 <div className="text-lg font-bold text-white leading-none">{squeezeData.Squeeze_Total_Pool_BQ?.toLocaleString() || 0}</div>
                                 <div className="text-[10px] uppercase text-slate-500 font-bold mt-1">Squeeze Opportunities</div>
                             </div>
                        </div>

                        <div className="bg-slate-900/40 border border-slate-700/50 rounded-lg p-3 flex items-center gap-3 hover:border-slate-600 transition-colors">
                             <div className="p-2 bg-slate-800/80 rounded-md"><UserCheck size={16} className="text-emerald-500"/></div>
                             <div>
                                 <div className="text-lg font-bold text-white leading-none">{targeting.supporter_base?.toLocaleString() || 0}</div>
                                 <div className="text-[10px] uppercase text-slate-500 font-bold mt-1">Base (GOTV)</div>
                             </div>
                        </div>
                    </div>

                    {/* COL 2: SQUEEZE CHART */}
                    <div className="bg-slate-900/40 rounded-lg p-4 border border-slate-700/50 flex flex-col">
                        <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Squeeze Composition</h4>
                        <div className="space-y-4 flex-grow">
                            {topSqueezeTargets.map(([party, score]) => (
                                <div key={party}>
                                    <div className="flex justify-between text-xs mb-1.5">
                                        <span className={`font-bold capitalize ${getPartyTextColor(party)}`}>{party}</span>
                                        <span className="text-slate-400 font-mono">{(score * 100).toFixed(1)}%</span>
                                    </div>
                                    <div className="h-2 w-full bg-slate-800 rounded-full overflow-hidden border border-slate-700/50">
                                        <div 
                                            className="h-full rounded-full opacity-90 shadow-[0_0_10px_rgba(0,0,0,0.3)]" 
                                            style={{ width: `${score*100}%`, backgroundColor: getPartyColor(party) }}
                                        />
                                    </div>
                                </div>
                            ))}
                            {topSqueezeTargets.length === 0 && <div className="text-xs text-slate-600 italic">No squeeze data.</div>}
                        </div>
                    </div>

                    {/* COL 3: ARCHETYPES */}
                    <div className="space-y-3">
                         <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Primary Targets</h4>
                         {priorityArchetypes.slice(0, 2).map((arch, i) => (
                             <div key={i} className="bg-slate-900/40 p-3 rounded-lg border border-slate-700/50 hover:border-indigo-500/50 transition-all hover:bg-slate-900/60 group">
                                 <div className="flex justify-between items-start mb-2">
                                     <span className="text-xs font-bold text-slate-200 group-hover:text-indigo-300 transition-colors">{arch.name}</span>
                                     <span className="text-[9px] px-1.5 py-0.5 bg-slate-950 rounded text-slate-400 border border-slate-800">
                                         Eff: {((1 - (arch.campaign_targeting?.activation_difficulty || 0)) * 100).toFixed(0)}%
                                     </span>
                                 </div>
                                 <div className="flex items-center gap-4 text-[10px] text-slate-500">
                                     <div className="flex items-center gap-1">
                                         <Users size={10} /> {arch.population_metrics?.estimated_population}
                                     </div>
                                     <div className="flex items-center gap-1">
                                         <Vote size={10} /> {(arch.campaign_targeting?.persuadability_score * 100).toFixed(0)}% Persuadable
                                     </div>
                                 </div>
                             </div>
                         ))}
                    </div>

                </div>
            </GlassContainer>
        </div>
    );
};

export const renderOverview = (props) => <Overview {...props} />;